/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

// Add third-party dependencies.
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Business,
  Settings,
  Group,
  Add,
  AccountBalance,
  AccountCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import type { DashboardOverviewResponse } from '../../../../types/dashboard';
import type { Studio } from '../../../../types/studio';
import { generateOwnerQuickActions } from '../../../../utils/dashboardHelpers';

interface QuickActionsCardProps {
  dashboardData?: DashboardOverviewResponse;
  studiosData?: {
    studios: Studio[];
    total: number;
  };
}

/**
 * Quick actions card component for the dashboard.
 * Provides navigation to key pages and features.
 *
 * @example
 * return (
 *   <QuickActionsCard dashboardData={dashboardData} studiosData={studiosData} />
 * );
 *
 * @returns {React.ReactElement} The quick actions card component
 */
const QuickActionsCard: React.FC<QuickActionsCardProps> = ({
  dashboardData,
  studiosData,
}) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  const studioCount = studiosData?.total || 0;
  const hasNoStudios = studioCount === 0;
  const hasOneStudio = studioCount === 1;
  const hasMultipleStudios = studioCount > 1;

  // Get backend data
  const userSummary = dashboardData?.data?.user_summary;
  const studios = dashboardData?.data?.studios || [];
  const backendQuickActions = dashboardData?.data?.quick_actions || [];

  const handleComingSoon = () => {
    // Placeholder for future functionality
  };

  // Determine studio action description
  const getStudioDescription = () => {
    if (hasNoStudios) return t('quickActions.actionDescriptions.createStudio');
    if (hasOneStudio) return t('quickActions.actionDescriptions.manageStudio');
    return t('quickActions.actionDescriptions.manageStudios');
  };

  // Get icon for action type
  const getActionIcon = (type: string) => {
    switch (type) {
      case 'verify_email':
        return <Settings />;
      case 'complete_profile':
        return <Settings />;
      case 'verify_phone':
        return <Settings />;
      case 'join_studio':
        return <Add />;
      case 'manage_members':
        return <Group />;
      default:
        return <Business />;
    }
  };

  // Core navigation actions (always available)
  const coreActions = [
    {
      id: 'profile',
      label: t('quickActions.actions.profile', {
        defaultValue: 'Profile',
      }),
      icon: <AccountCircle />,
      description: t('quickActions.actionDescriptions.completeProfile', {
        defaultValue: 'Complete your profile with photo, bio, and more',
      }),
      onClick: () => navigate('/profile'),
      disabled: false,
      priority: 'high',
    },
    {
      id: 'studios',
      label: hasNoStudios
        ? t('quickActions.actions.createStudio')
        : t('quickActions.actions.studios'),
      icon: hasNoStudios ? <Add /> : <Business />,
      description: getStudioDescription(),
      onClick: () => navigate(hasNoStudios ? '/studios/create' : '/studios'),
      disabled: false,
      priority: 'medium',
    },
    {
      id: 'tools',
      label: t('quickActions.actions.tools'),
      icon: <Group />,
      description: t('quickActions.actionDescriptions.accessTools'),
      onClick: () => navigate('/tools'),
      disabled: false,
      priority: 'medium',
    },
    {
      id: 'billing',
      label: t('quickActions.actions.billing'),
      icon: <AccountBalance />,
      description: t('quickActions.actionDescriptions.manageBilling'),
      onClick: () => navigate('/billing'),
      disabled: false,
      priority: 'low',
    },
    {
      id: 'settings',
      label: t('quickActions.actions.settings'),
      icon: <Settings />,
      description: t('quickActions.actionDescriptions.accountSettings'),
      onClick: () => navigate('/settings'),
      disabled: false,
      priority: 'low',
    },
  ];

  // Generate enhanced quick actions using backend data
  const enhancedQuickActions = userSummary
    ? generateOwnerQuickActions(userSummary, studios, backendQuickActions)
    : [];

  // Transform backend actions to common format
  const backendActionsTransformed = enhancedQuickActions
    .slice(0, 2)
    .map(action => ({
      id: action.type,
      label: action.label,
      icon: getActionIcon(action.type),
      description: action.description || '',
      onClick: () => navigate(action.url),
      disabled: false,
      priority: action.priority,
      comingSoon: false,
      source: 'backend' as const,
    }));

  // Transform core actions to common format
  const coreActionsTransformed = coreActions.slice(0, 5).map(action => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    description: action.description,
    onClick: action.onClick,
    disabled: action.disabled,
    priority: action.priority,
    comingSoon: false,
    source: 'frontend' as const,
  }));

  // Combine and deduplicate actions, prioritizing backend actions
  const seenIds = new Set<string>();
  const quickActionsConfig = [
    ...backendActionsTransformed, // Backend actions first (higher priority)
    ...coreActionsTransformed, // Frontend actions second
  ]
    .filter(action => {
      if (seenIds.has(action.id)) {
        return false; // Skip duplicate
      }
      seenIds.add(action.id);
      return true;
    })
    .slice(0, 6); // Limit to 6 total actions

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('quickActions.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {hasNoStudios && t('quickActions.description.noStudios')}
          {hasOneStudio &&
            studiosData?.studios[0] &&
            t('quickActions.description.oneStudio', {
              studioName: studiosData.studios[0].name,
            })}
          {hasMultipleStudios &&
            t('quickActions.description.multipleStudios', {
              count: studioCount,
            })}
          {!hasNoStudios &&
            !hasOneStudio &&
            !hasMultipleStudios &&
            t('quickActions.description.default')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 2,
          }}
        >
          {quickActionsConfig.map(action => (
            <Tooltip
              key={action.id}
              title={
                action.comingSoon ? action.description : action.description
              }
              arrow
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={
                    action.comingSoon ? handleComingSoon : action.onClick
                  }
                  disabled={action.disabled}
                  sx={{
                    width: '100%',
                    height: 56,
                    flexDirection: 'column',
                    gap: 0.5,
                    '& .MuiButton-startIcon': {
                      margin: 0,
                    },
                    opacity: action.disabled ? 0.6 : 1,
                    cursor: action.disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {action.label}
                  </Typography>
                </Button>
              </span>
            </Tooltip>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuickActionsCard;
